import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useClearData } from "@/hooks/use-clear-data";
import { AlertTriangle, Trash2, Loader2, RefreshCw, Users, Info } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { DestructiveConfirmModal } from "@/components/settings/DestructiveConfirmModal";

type ClearAction = "transactions" | "transactionsAndAccounts" | "all";

export function DangerZone() {
  const { isClearing, stats, sharingInfo, isLoadingStats, fetchStats, clearTransactions, clearTransactionsAndAccounts, clearAllData } = useClearData();
  const [activeAction, setActiveAction] = useState<ClearAction | null>(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const handleConfirm = async () => {
    if (!activeAction) return;
    switch (activeAction) {
      case "transactions":
        await clearTransactions();
        break;
      case "transactionsAndAccounts":
        await clearTransactionsAndAccounts();
        break;
      case "all":
        await clearAllData();
        break;
    }
    setActiveAction(null);
  };

  const getActionDescription = (action: ClearAction) => {
    switch (action) {
      case "transactions":
        return {
          title: "Limpar Transações",
          description: "Remove todas as transações, mantendo contas e metas",
          items: stats ? [`${stats.transactions} transações`, `${stats.recurrences} recorrências`] : [],
        };
      case "transactionsAndAccounts":
        return {
          title: "Limpar Transações e Contas",
          description: "Remove transações e contas, mantendo metas e categorias",
          items: stats ? [`${stats.transactions} transações`, `${stats.accounts} contas`, `${stats.recurrences} recorrências`] : [],
        };
      case "all":
        return {
          title: "Apagar Todos os Dados",
          description: "Remove TUDO e permite recomeçar do zero",
          items: stats ? [
            `${stats.transactions} transações`,
            `${stats.accounts} contas`,
            `${stats.goals} metas`,
            `${stats.categories} categorias personalizadas`,
            `${stats.budgets} orçamentos`,
          ] : [],
        };
    }
  };

  const ActionButton = ({ action, variant = "outline" }: { action: ClearAction; variant?: "outline" | "destructive" }) => {
    const info = getActionDescription(action);

    return (
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-lg border border-destructive/20 bg-destructive/5">
        <div className="space-y-1">
          <h4 className="font-medium text-sm">{info.title}</h4>
          <p className="text-xs text-muted-foreground">{info.description}</p>
        </div>
        <Button
          variant={variant}
          size="sm"
          onClick={() => setActiveAction(action)}
          disabled={isClearing}
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Limpar...
        </Button>
      </div>
    );
  };

  return (
    <Card className="border-destructive/50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-destructive">
          <AlertTriangle className="h-5 w-5" />
          Zona de Perigo
        </CardTitle>
        <CardDescription>
          Ações irreversíveis. Tenha certeza antes de prosseguir.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoadingStats ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
              <span>Dados na sua conta:</span>
              <Button variant="ghost" size="sm" onClick={fetchStats}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Atualizar
              </Button>
            </div>

            {/* Sharing warning */}
            {sharingInfo && (sharingInfo.hasSharedWithOthers || sharingInfo.hasAccessToShared) && (
              <Alert className="mb-6 border-blue-500/50 bg-blue-50 dark:bg-blue-950">
                <Users className="h-4 w-4 text-blue-600" />
                <AlertTitle className="text-blue-600">Conta compartilhada</AlertTitle>
                <AlertDescription className="text-blue-600/80">
                  {sharingInfo.hasSharedWithOthers && (
                    <p className="text-sm">
                      Você compartilhou sua conta com {sharingInfo.sharedWithCount} pessoa{sharingInfo.sharedWithCount > 1 ? 's' : ''}.
                      Ao limpar seus dados, essas pessoas perderão acesso aos dados removidos.
                    </p>
                  )}
                  {sharingInfo.hasAccessToShared && (
                    <p className="text-sm mt-1">
                      <Info className="h-3 w-3 inline mr-1" />
                      Você tem acesso a {sharingInfo.accessToCount} conta{sharingInfo.accessToCount > 1 ? 's' : ''} compartilhada{sharingInfo.accessToCount > 1 ? 's' : ''}.
                      Esses dados <strong>não serão afetados</strong> - apenas seus próprios dados serão removidos.
                    </p>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {stats && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 rounded-lg bg-muted/50 mb-6">
                <div className="text-center">
                  <div className="text-2xl font-bold">{stats.transactions}</div>
                  <div className="text-xs text-muted-foreground">Transações</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{stats.accounts}</div>
                  <div className="text-xs text-muted-foreground">Contas</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{stats.goals}</div>
                  <div className="text-xs text-muted-foreground">Metas</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{stats.categories}</div>
                  <div className="text-xs text-muted-foreground">Categorias</div>
                </div>
              </div>
            )}

            {/* Shared data info */}
            {stats && (stats.sharedTransactions > 0 || stats.sharedAccounts > 0) && (
              <div className="text-sm text-muted-foreground mb-4 p-3 rounded-lg bg-muted/30 border">
                <Users className="h-4 w-4 inline mr-2" />
                Você também tem acesso a <strong>{stats.sharedTransactions}</strong> transações e <strong>{stats.sharedAccounts}</strong> contas compartilhadas por outros usuários (não serão afetadas).
              </div>
            )}

            <ActionButton action="transactions" />
            <ActionButton action="transactionsAndAccounts" />
            <ActionButton action="all" variant="destructive" />

            {activeAction && (
              <DestructiveConfirmModal
                open={activeAction !== null}
                onOpenChange={(open) => { if (!open) setActiveAction(null); }}
                title={getActionDescription(activeAction)!.title}
                description={getActionDescription(activeAction)!.description}
                items={getActionDescription(activeAction)!.items}
                onConfirm={handleConfirm}
                isLoading={isClearing}
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
